import { Button, Grid } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { useMemo } from 'react';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import { ActionButton } from '../../Button';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { useAuthContext } from '../../../contexts/AuthContext';
import DefaultConstants from '../../../data/Constants';
import { MoreHoriz } from '@mui/icons-material';
import useState from '../../../hooks/useState';
import useDialog from '../../../hooks/useDialog';
import ServiceOrdersList from '../../ItemsList/ServiceOrdersList';
import Empty from '../../Empty';
import AddDialog from '../../ServiceOrder/AddDialog';
import IServiceOrder from '../../../interfaces/IServiceOrder';
import ServiceOrderInformationsDialog from '../../ServiceOrder/ViewDialog/ServiceOrderInformationsDialog';
import ViewAttachmentsDialog from '../../ServiceOrder/ViewDialog/ViewAttachmentsDialog';
import { ServicePropertyData, ServicePropertyEnum } from '../../../data/Enums';
import Tooltip from '../../Tooltip';
import Badge from '../../Badge';


const attachmentPropertyData = ServicePropertyData[ServicePropertyEnum.Attachments];


interface IViewServiceOrdersDialogProps extends IDefaultDialogProps {
    room: IRoom;
    handleRefreshSelectedRoom: () => void;
}

interface IStateValues {
    selectedItem: IServiceOrder | undefined;
}


const ViewServiceOrdersDialog: React.FC<IViewServiceOrdersDialogProps> = ({
    open,
    handleClose,
    room,
    handleRefreshSelectedRoom
}) => {
    const { translate } = useLanguageContext();
    const { hasPermission } = useAuthContext();


    const state = useState<IStateValues>({
        selectedItem: undefined
    });


    const informationsDialog = useDialog();
    const viewAttachmentsDialog = useDialog();
    
    const addServiceOrderDialog = useDialog();


    const dialogActions = useMemo(() => {
        const elements = [
            <ActionButton action='close' onClick={handleClose} />,
        ];

        if (hasPermission('Governance.RoomOverview.ServiceOrder.Add')) {
            elements.push(<ActionButton action='add' onClick={() => addServiceOrderDialog.handleOpen()} />);
        }

        return elements;
        // eslint-disable-next-line
    }, [translate]);

    
    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={translate('serviceOrders')}
                dialogActions={dialogActions}
                className={`dialogactions-spacebetween overflow-inherit`}
                absolute={true}
                dividers={true}
            >
                <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                    <Grid item xs={12}>
                        {
                            room.serviceOrders.length === 0 ? (
                                <Empty text="noRecordsFound" />
                            ) : (
                                <ServiceOrdersList 
                                    items={room.serviceOrders}
                                    actions={(item) => {
                                        if (!hasPermission('Governance.RoomOverview.ServiceOrder.View')) {
                                            return <></>;
                                        }

                                        return <>
                                            <Tooltip title={translate('informations')}>
                                                <Button 
                                                    className='item-action backgroundcolor-grey-2 text-color-1'
                                                    onClick={() => {
                                                        informationsDialog.handleOpen();
                                                        state.update('selectedItem', item);
                                                    }}
                                                >
                                                    <MoreHoriz />
                                                </Button>
                                            </Tooltip>

                                            <Tooltip title={translate(attachmentPropertyData.name)}>
                                                <Badge 
                                                    badgeContent={item.attachments?.length ?? 0} 
                                                    showZero={true} 
                                                    className='backgroundcolor-secondary color-white'
                                                >
                                                    <Button 
                                                        className='item-action backgroundcolor-grey-2 text-color-1'
                                                        onClick={() => {
                                                            viewAttachmentsDialog.handleOpen();
                                                            state.update('selectedItem', item);
                                                        }}
                                                    >
                                                        <attachmentPropertyData.Icon />
                                                    </Button>
                                                </Badge>
                                            </Tooltip>
                                        </>;
                                    }}
                                />
                            )
                        }
                    </Grid>
                </Grid>
            </Dialog>

            <AddDialog 
                open={addServiceOrderDialog.open}
                handleClose={() => addServiceOrderDialog.handleClose()}
                handleListRefresh={() => handleRefreshSelectedRoom()}
                room={room}
            />

            <ServiceOrderInformationsDialog 
                open={state.values.selectedItem !== undefined && informationsDialog.open}
                handleClose={() => {
                    informationsDialog.handleClose()
                    state.update('selectedItem', undefined);
                }}
                serviceOrder={state.values.selectedItem}
                editDepartment={false}
                editPriority={false}
                editResponsible={false}
                editStartDate={false}
                editEstimatedCompletionDate={false}
                handleRefreshSelectedServiceOrder={() => {}}
            />

            <ViewAttachmentsDialog 
                open={state.values.selectedItem !== undefined && viewAttachmentsDialog.open}
                handleClose={() => {
                    viewAttachmentsDialog.handleClose();
                    state.update('selectedItem', undefined);
                }}
                attachments={state.values.selectedItem?.attachments ?? []}
            />
        </>
    );
}

export default ViewServiceOrdersDialog;